import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

export function AdminHome() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/mi/users")
      .then((r) => setUsers(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const grouped = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="admin-home">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.common.administrator}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{t.admin.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{t.admin.sub}</p>
      </header>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 lg:grid-cols-3">
        <Stat label={t.common.member} value={grouped.member || 0} testid="stat-members" />
        <Stat label={t.common.clinician} value={grouped.clinician || 0} testid="stat-clinicians" />
        <Stat label={t.common.administrator} value={grouped.administrator || 0} testid="stat-admins" />
      </div>

      <div className="mios-card" data-testid="admin-users-table">
        <div className="mios-card-header">
          <div className="mios-card-title">{t.admin.users}</div>
          <div className="mios-card-sub">{users.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-[0.06em] text-slate-500">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Lang</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-5 py-3 text-slate-600 mono">{u.email}</td>
                  <td className="px-5 py-3"><span className="mios-pill mios-pill-normal">{u.role}</span></td>
                  <td className="px-5 py-3 text-slate-600 uppercase">{u.language}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function AdminBrainStatus() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/brain/admin/status")
      .then((r) => setStatus(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-brain-status">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.layer.brain} · {t.layer.brainSub}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{t.admin.brainStatus}</h1>
      </header>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {!status ? (
        <div className="text-sm text-slate-400">{t.common.loading}</div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="mios-card" data-testid="brain-layers-card">
              <div className="mios-card-header"><div className="mios-card-title">{t.admin.layers}</div></div>
              <div className="mios-card-body space-y-2 text-sm">
                {Object.entries(status.layers).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-700 capitalize">{k}</span>
                    <span className="mios-pill mios-pill-normal">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mios-card" data-testid="brain-policies-card">
              <div className="mios-card-header"><div className="mios-card-title">{t.admin.policies}</div></div>
              <div className="mios-card-body space-y-2 text-sm">
                {Object.entries(status.policies).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-700">{k.replaceAll("_", " ")}</span>
                    <span className={`mios-pill ${v ? "mios-pill-normal" : "mios-pill-high"}`}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mios-card" data-testid="brain-routing-card">
            <div className="mios-card-header"><div className="mios-card-title">{t.admin.routing}</div></div>
            <div className="mios-card-body overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] uppercase tracking-[0.06em] text-slate-500">
                    <th className="py-2 pr-4 font-medium">task</th>
                    <th className="py-2 pr-4 font-medium">provider</th>
                    <th className="py-2 pr-4 font-medium">model</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(status.routing).map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4 capitalize text-slate-700">{k}</td>
                      <td className="py-2 pr-4 mono text-slate-900">{v.provider}</td>
                      <td className="py-2 pr-4 mono text-slate-700">{v.model}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mios-card" data-testid="brain-providers-card">
            <div className="mios-card-header"><div className="mios-card-title">{t.admin.providers}</div></div>
            <div className="mios-card-body">
              <ul className="space-y-2 text-sm">
                {status.providers.map((p) => (
                  <li key={p.name} className="flex items-center justify-between">
                    <span className="font-medium text-slate-900 capitalize">{p.name}</span>
                    <div className="flex items-center gap-2">
                      {p.is_default && <span className="mios-pill mios-pill-low">default</span>}
                      <span className={`mios-pill ${p.available ? "mios-pill-normal" : "mios-pill-warning"}`}>
                        {p.available ? t.admin.available : t.admin.unavailable}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="mios-card" data-testid={testid}>
      <div className="mios-card-body">
        <div className="mios-card-sub">{label}</div>
        <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 mono">{value}</div>
      </div>
    </div>
  );
}
